import { manufacturingFetch } from "@/lib/manufacturingClient";
import type {
  MfgOverheadBucketCreateResponse,
  MfgOverheadBucketsListResponse,
  MfgOverheadBucketUpdateResponse,
  MfgOverheadPeriodActualResponse,
  MfgOverheadPeriodAssumedResponse,
  MfgOverheadPeriodSummaryResponse,
  MfgOverheadPeriodVarianceResponse,
  MfgOverheadRateResponse,
  MfgPeriodCloseFinalizeResponse,
  MfgPeriodCloseReopenResponse,
  MfgPeriodCloseStartResponse,
} from "@/lib/manufacturing/overheadTypes";
import type {
  OverheadCogsBasis,
  OverheadVarianceDisplay,
} from "@/lib/manufacturing/overheadCogsLink";

function buildOverheadPeriodQuery(filters: { plant_id?: string; period_start?: string }): string {
  const qs = new URLSearchParams();
  if (filters.plant_id) qs.set("plant_id", filters.plant_id);
  if (filters.period_start) qs.set("period_start", filters.period_start);
  const suffix = qs.toString();
  return suffix ? `?${suffix}` : "";
}

export async function listManufacturingOverheadBuckets() {
  return manufacturingFetch<MfgOverheadBucketsListResponse>("/api/mfg/overhead/buckets", {
    method: "GET",
  });
}

export async function createManufacturingOverheadBucket(body: {
  bucket_key?: string;
  name: string;
  bucket_type_id?: string;
  type_key?: string;
  parent_bucket_id?: string | null;
  category?: string;
  is_active?: boolean;
  sort_order?: number;
  notes?: string;
  is_aggregate?: boolean;
  tracks_balance?: boolean;
  balance_start_usd?: number | null;
  balance_as_of?: string | null;
  principal_monthly_usd?: number | null;
}) {
  return manufacturingFetch<MfgOverheadBucketCreateResponse>("/api/mfg/overhead/buckets/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateManufacturingOverheadBucket(body: {
  bucket_id: string;
  name?: string;
  bucket_type_id?: string;
  parent_bucket_id?: string | null;
  category?: string;
  is_active?: boolean;
  sort_order?: number;
  notes?: string;
  is_aggregate?: boolean;
  tracks_balance?: boolean;
  balance_start_usd?: number | null;
  balance_as_of?: string | null;
  principal_monthly_usd?: number | null;
  retire?: boolean;
  plant_id?: string;
}) {
  return manufacturingFetch<MfgOverheadBucketUpdateResponse>("/api/mfg/overhead/buckets/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteManufacturingOverheadBucket(body: { bucket_id: string }) {
  return manufacturingFetch<{ ok: true }>("/api/mfg/overhead/buckets/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function saveManufacturingOverheadConsumption(body: {
  plant_id: string;
  uses_last_day: boolean;
  consumption_day: number | null;
}) {
  return manufacturingFetch<{ ok: true }>("/api/mfg/overhead/consumption", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function getManufacturingOverheadPeriodSummary(filters: {
  plant_id: string;
  period_start?: string;
}) {
  return manufacturingFetch<MfgOverheadPeriodSummaryResponse>(
    `/api/mfg/overhead/period-summary${buildOverheadPeriodQuery(filters)}`,
    { method: "GET" },
  );
}

export async function setManufacturingOverheadPeriodAssumed(body: {
  plant_id: string;
  bucket_id: string;
  period_start?: string;
  assumed_value_usd: number | null;
  assumed_update_reason: string;
}) {
  return manufacturingFetch<MfgOverheadPeriodAssumedResponse>("/api/mfg/overhead/period-assumed", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function bookManufacturingOverheadPeriodActual(body: {
  plant_id: string;
  bucket_id: string;
  period_start?: string;
  actual_value_usd: number;
}) {
  return manufacturingFetch<MfgOverheadPeriodActualResponse>("/api/mfg/overhead/period-actuals", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function getManufacturingOverheadSnapshotRate(plant_id: string) {
  const qs = new URLSearchParams({ plant_id });
  return manufacturingFetch<MfgOverheadRateResponse>(`/api/mfg/overhead/rate?${qs.toString()}`, {
    method: "GET",
  });
}

export async function getManufacturingOverheadPeriodVariance(filters: {
  plant_id: string;
  period_start?: string;
}) {
  return manufacturingFetch<MfgOverheadPeriodVarianceResponse>(
    `/api/mfg/overhead/period-variance${buildOverheadPeriodQuery(filters)}`,
    { method: "GET" },
  );
}

export async function startManufacturingPeriodClose(body: { plant_id: string; period_start?: string }) {
  return manufacturingFetch<MfgPeriodCloseStartResponse>("/api/mfg/period-close/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function finalizeManufacturingPeriodClose(body: {
  plant_id: string;
  period_start?: string;
  copy_actuals_to_next_period_assumed?: boolean;
}) {
  return manufacturingFetch<MfgPeriodCloseFinalizeResponse>("/api/mfg/period-close/finalize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function reopenManufacturingPeriodClose(body: {
  plant_id: string;
  period_start?: string;
  reopen_reason: string;
}) {
  return manufacturingFetch<MfgPeriodCloseReopenResponse>("/api/mfg/period-close/reopen", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type MfgOverheadCogsLinkSettings = {
  basis: OverheadCogsBasis | null;
  show_on_cogs: boolean;
  variance_display: OverheadVarianceDisplay;
  goal_rate: number | null;
};

export type MfgOverheadCogsPreview = {
  plant_id: string | null;
  plant_name: string;
  period_start: string;
  using_default_plant: boolean;
  goal_rate: number | null;
  actual_rate: number | null;
  variance_rate: number | null;
  activity: number;
  note: string;
};

export type MfgOverheadCogsLinkResponse = {
  ok: true;
  settings: MfgOverheadCogsLinkSettings;
  missing_table?: boolean;
  preview: MfgOverheadCogsPreview | null;
};

export async function getManufacturingOverheadCogsLink(filters: {
  plant_id?: string;
  period_start?: string;
}) {
  return manufacturingFetch<MfgOverheadCogsLinkResponse>(
    `/api/mfg/overhead/cogs-link${buildOverheadPeriodQuery(filters)}`,
    { method: "GET" },
  );
}

export async function saveManufacturingOverheadCogsLink(body: {
  basis: OverheadCogsBasis | null;
  show_on_cogs: boolean;
  variance_display: OverheadVarianceDisplay;
  goal_rate: number | null;
  plant_id?: string;
  period_start?: string;
}) {
  return manufacturingFetch<MfgOverheadCogsLinkResponse>("/api/mfg/overhead/cogs-link/save", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type {
  MfgBucketType,
  MfgOverheadBucket,
  MfgOverheadBucketTreeNode,
  MfgOverheadPeriodBucketRow,
  MfgOverheadPeriodSummary,
  MfgOverheadPeriodVarianceReport,
  MfgOverheadSnapshotRate,
  MfgPeriodCloseRow,
} from "@/lib/manufacturing/overheadTypes";
